import { useEffect, useId, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { TextField } from '@/components/ui/TextField';
import { useToast } from '@/components/ui/toast';

import { ENTITY_COPY, savedChanges } from '../copy';
import { catalogError, normalizeNormCode, requiredTextError } from '../presentation';
import type { NormClauseRow } from '../types';
import { useSaveCatalogItem } from '../use-catalogs-admin';

const COPY = ENTITY_COPY.norm;

interface Form {
  normCode: string;
  clause: string;
  title: string;
}

/** Alta o edición de una cláusula. Una norma nueva (p. ej. ISO 14001) nace con su
 *  primera cláusula; las existentes se sugieren para no escribirlas distinto. Norma
 *  y cláusula identifican la clasificación: al editar solo cambia el requisito. */
export function NormModal({
  open,
  editing,
  normCodes,
  onClose,
}: {
  open: boolean;
  editing: NormClauseRow | null;
  /** Normas ya existentes, para sugerirlas. */
  normCodes: readonly string[];
  onClose: () => void;
}): ReactElement | null {
  const formId = useId();
  const normFieldId = useId();
  const listId = useId();
  const save = useSaveCatalogItem('norm-catalog');
  const { showToast } = useToast();
  const [form, setForm] = useState<Form>({ normCode: '', clause: '', title: '' });
  const [checked, setChecked] = useState(false);
  const [server, setServer] = useState<ReturnType<typeof catalogError> | null>(null);
  const isNew = editing === null;

  useEffect(() => {
    if (open) {
      setForm({
        normCode: editing?.normCode ?? '',
        clause: editing?.clause ?? '',
        title: editing?.title ?? '',
      });
      setChecked(false);
      setServer(null);
    }
  }, [open, editing]);

  const set = (patch: Partial<Form>): void => {
    setForm((f) => ({ ...f, ...patch }));
    setServer(null);
  };
  const normMsg =
    checked && isNew ? requiredTextError(form.normCode, 40, 'Escribe la norma') : null;
  const clauseMsg =
    server?.field === 'code'
      ? server.message
      : checked && isNew
        ? requiredTextError(form.clause, 40, 'Escribe la cláusula')
        : null;
  const titleMsg = checked ? requiredTextError(form.title, 300, 'Escribe el requisito') : null;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setChecked(true);
    const invalid =
      (isNew &&
        (requiredTextError(form.normCode, 40, '') !== null ||
          requiredTextError(form.clause, 40, '') !== null)) ||
      requiredTextError(form.title, 300, '') !== null;
    if (invalid || save.isPending) {
      return;
    }
    const title = form.title.trim();
    try {
      if (editing === null) {
        const normCode = normalizeNormCode(form.normCode);
        const clause = form.clause.trim();
        await save.mutateAsync({ id: null, body: { normCode, clause, title } });
        showToast(COPY.created(`${normCode} ${clause}`));
      } else if (title !== editing.title) {
        // Solo el requisito es editable; sin cambios se cierra sin llamar al API.
        await save.mutateAsync({ id: editing.id, body: { title } });
        showToast(savedChanges(`${editing.normCode} ${editing.clause}`));
      }
      onClose();
    } catch (err) {
      setServer(catalogError(err, 'norm'));
    }
  }

  return (
    <ModalShell
      open={open}
      title={isNew ? COPY.newTitle : COPY.editTitle}
      busy={save.isPending}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onClose} disabled={save.isPending}>
            Cancelar
          </button>
          <button type="submit" form={formId} className="btn sm" disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : isNew ? COPY.createLabel : 'Guardar cambios'}
          </button>
        </>
      }
    >
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        <div className="fld">
          <label htmlFor={normFieldId} className="fld-lab">
            Norma
          </label>
          <input
            id={normFieldId}
            className="selector fw mono"
            value={form.normCode}
            maxLength={40}
            placeholder="ISO9001"
            list={listId}
            autoComplete="off"
            disabled={!isNew}
            aria-invalid={normMsg !== null}
            aria-describedby={normMsg === null ? undefined : `${normFieldId}-err`}
            onChange={(e) => set({ normCode: e.target.value })}
          />
          <datalist id={listId}>
            {normCodes.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          {normMsg !== null && (
            <span id={`${normFieldId}-err`} className="fld-err">
              {normMsg}
            </span>
          )}
        </div>
        <TextField
          label="Cláusula"
          value={form.clause}
          maxLength={40}
          placeholder="7.5"
          mono
          disabled={!isNew}
          error={clauseMsg}
          onChange={(clause) => set({ clause })}
        />
        <TextField
          label="Requisito"
          value={form.title}
          maxLength={300}
          placeholder="Información documentada"
          error={titleMsg}
          onChange={(title) => set({ title })}
        />
        {server !== null && server.field === null && (
          <p className="fld-error" role="alert">
            {server.message}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
